import { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, LifeBuoy, MapPin, Phone, ShieldCheck, ShoppingBag, Waves } from "lucide-react";
import Seo, { marinaJsonLd } from "../components/Seo";
import PageHero from "../components/PageHero";
import BookingWidget, { todayISO } from "../components/BookingWidget";
import FleetStrip from "../components/FleetStrip";
import WeatherBanner from "../components/WeatherBanner";
import MailingList from "../components/MailingList";
import { Section, SectionHeading, SplitFeature, InfoCard } from "../components/Section";
import Reveal from "../components/Reveal";
import { LOCATIONS } from "../lib/content";
import { useHours } from "../hooks/useHours";

const WB = LOCATIONS["west-branch"];
const LM = LOCATIONS["lake-milton"];

const VALUE_PROPS = [
  { icon: Waves, title: "Two Ohio state parks", body: "Rentals at West Branch State Park and Lake Milton State Park." },
  { icon: Anchor, title: "Pontoons to jet skis", body: "Standard and deluxe pontoons, VIP tritoons, waverunners, kayaks and fishing boats." },
  { icon: ShieldCheck, title: "Clear rental rules", body: "18+ with a valid driver's license, plus Ohio boater education where required." },
  { icon: LifeBuoy, title: "Flexible cancellation", body: "Add the $20 Flexibility Fee at booking to cancel up to 24 hours ahead for a full rental refund." },
];

const PLAN_CARDS = [
  { to: "/lake-information", icon: MapPin, title: "Lake information", body: "Launch ramps and courtesy docks at West Branch State Park." },
  { to: "/rental-requirements", icon: ShieldCheck, title: "Rental requirements", body: "Age, licensing, boater education, cancellation and weather policies." },
  { to: "/storage-and-docking", icon: Anchor, title: "Storage & docking", body: "Courtesy docks at $20 per night." },
  { to: "/supplies", icon: ShoppingBag, title: "Supplies", body: "Boating equipment, fishing bait and maintenance supplies." },
];

export default function Home() {
  const [date] = useState(todayISO());
  const { locations, hoursFor, loading } = useHours();
  const wbHours = hoursFor("west-branch");
  const [pickedSlug, setPickedSlug] = useState(null);
  const locationSlug = pickedSlug ?? (locations || []).find((l) => l.enabled && l.locationId != null)?.slug;

  return (
    <>
      <Seo
        title="West Branch Marina | Pontoon, Jet Ski & Kayak Rentals in Ohio"
        description="Rent pontoons, VIP tritoons, jet skis, kayaks and fishing boats at West Branch State Park and Lake Milton State Park, Ohio. Book online or call (330) 296-9209."
        path="/"
        jsonLd={marinaJsonLd(WB)}
      />

      <PageHero
        image="/images/hero_pontoon_golden.jpeg"
        alt="Pontoon boat cruising across West Branch lake in golden evening light"
        eyebrow="West Branch & Lake Milton State Parks"
        title="Your day on the lake starts here"
        subtitle="Pontoons, high-horsepower tritoons, jet skis and kayaks on two Ohio lakes. Pick your date and see exactly what's on the water."
      >
        <div className="max-w-2xl">
          <BookingWidget variant="hero" slug={locationSlug} onSlugChange={setPickedSlug} />
        </div>
      </PageHero>

      <Section tone="white" className="!py-12 lg:!py-14">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE_PROPS.map((v, i) => (
            <Reveal key={v.title} delay={i * 70} data-testid={`value-prop-${i}`}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
                <v.icon className="h-5 w-5 text-ember" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-lg font-semibold text-navy">{v.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <FleetStrip date={date} selectedSlug={locationSlug} onSelectSlug={setPickedSlug} />

      <Section tone="white">
        <SplitFeature
          testId="home-watersports-feature"
          image="/images/watersports_tube.jpeg"
          alt="Rider being towed on an inflatable tube behind a pontoon boat"
          eyebrow="Watersports"
          title="Towing is for the VIP and deluxe boats"
        >
          <p>
            Tubes, skis and wakeboards can only be towed behind select vessels — our VIP high-horsepower and deluxe
            pontoon models. If watersports are the whole point of your day, choose one of those.
          </p>
          <p>
            Prefer your own throttle? Jet skis and waverunners are available too, and kayaks are there when the water is
            glass and you'd rather paddle.
          </p>
          <Link
            to="/rental-requirements"
            data-testid="home-watersports-requirements-link"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-surface"
          >
            Read the rental rules
          </Link>
        </SplitFeature>
      </Section>

      <Section>
        <div className="mt-0">
          <SplitFeature
            testId="home-jetski-feature"
            reverse
            image="/images/jetski_action.jpeg"
            alt="Jet ski rider cutting across open water"
            eyebrow="Personal watercraft"
            title="Jet skis and waverunners"
          >
            <p>
              Fast, simple and easy to share between riders. Personal watercraft rentals follow the same rules as our
              boats: 18 or older, valid driver's license, and Ohio boater education where it applies.
            </p>
            <Link
              to="/boat-rentals/west-branch"
              data-testid="home-jetski-rentals-link"
              className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-ember-hover"
            >
              See what's available
            </Link>
          </SplitFeature>
        </div>
      </Section>

      <Section tone="white">
        <SectionHeading
          eyebrow="Plan your day"
          title="Everything else you need to know"
          intro="Ramps, rules, dockage and supplies — the practical details that make the day run smoothly."
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLAN_CARDS.map((c, i) => (
            <Reveal key={c.to} delay={i * 70}>
              <Link
                to={c.to}
                data-testid={`plan-card-${c.to.replace("/", "")}`}
                className="group block h-full rounded-2xl border border-sand-border bg-sand p-6 transition-all hover:-translate-y-1 hover:border-lake/40 hover:shadow-xl hover:shadow-navy/10"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-lake/10">
                  <c.icon className="h-5 w-5 text-lake" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-navy group-hover:text-lake transition-colors">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.body}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <section className="relative isolate overflow-hidden bg-navy grain">
        <img
          src="/images/lake_aerial.jpeg"
          alt="Aerial view of the lake and shoreline at West Branch State Park"
          loading="lazy"
          width={1264}
          height={848}
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 band-scrim" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-24">
          <SectionHeading
            tone="light"
            eyebrow="Two locations"
            title="Call us, or book the West Branch fleet online"
            intro="Both marinas operate the same way. Online booking is live at West Branch; Lake Milton reservations are taken over the phone."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {[WB, LM].map((loc) => {
              const h = hoursFor(loc.slug);
              return (
                <Reveal
                  key={loc.slug}
                  data-testid={`home-location-card-${loc.slug}`}
                  className="rounded-2xl border border-white/15 bg-white/[0.07] p-7 backdrop-blur-md"
                >
                  <p className="eyebrow text-lake">{loc.park}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold text-white">{loc.name}</h3>
                  <p className="mt-3 text-sm text-white/75" data-testid={`home-hours-${loc.slug}`}>
                    {h
                      ? `Daily ${h.open} – ${h.close}`
                      : loading
                        ? "Hours loading…"
                        : "Call for this season's hours"}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={loc.phoneHref}
                      data-testid={`home-call-${loc.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-sand-card"
                    >
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      {loc.phone}
                    </a>
                    <Link
                      to={`/boat-rentals/${loc.slug}`}
                      data-testid={`home-location-link-${loc.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
                    >
                      {loc.onlineBooking ? "Book online" : "Location details"}
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
          {wbHours?.source === "api" && (
            <p className="mt-8 text-xs text-white/50">
              Hours shown are pulled live from our booking system, so they always match what you can actually reserve.
            </p>
          )}
        </div>
      </section>

      {/* Source-equivalent position: weather, then the mailing list, at the foot
          of the home page. The home page has no marina context, so the banner
          defaults to West Branch -- as the source widget did. */}
      <WeatherBanner location="west-branch" title="West Branch Marina weather" />
      <MailingList footnote="It's 100% free and we will never share your information with anyone" />
    </>
  );
}
